import { useId } from "react";
import { useFetcher } from "react-router";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { type SignupResult, type SignupSource } from "#app/utils/mailing-list.server.ts";
import { useNonce } from "#app/utils/nonce-provider.ts";
import { Emblem } from "./ui/emblem.tsx";
import { Icon } from "./ui/icon.tsx";

export type SignupActionData = SignupResult & { email?: string }

/**
 * Mailing list signup. Submits to the /subscribe route action from any page.
 * Pass `actionData` only on /subscribe itself, so the result still shows when
 * the form was posted without JavaScript. `source` controls how the signup is
 * tagged in Shopify (see SOURCE_TAGS in mailing-list.server.ts).
 */
export default function MailingListForm({
  actionData,
  source = "website",
}: {
  actionData?: SignupActionData
  source?: SignupSource
}) {
  const fetcher = useFetcher<SignupActionData>()
  const nonce = useNonce()
  const errorId = useId()
  const result = fetcher.data ?? actionData
  const isSubmitting = fetcher.state !== "idle"
  const errorMessage = result && !result.ok ? result.message : undefined

  if (result?.ok) {
    return (
      <div role="status" className="flex flex-col items-center gap-4 py-4 text-center">
        <Emblem className="size-16 text-white" />
        <p className="text-2xl font-bold uppercase">You're on the list!</p>
        {result.email && <p className="text-sm italic">{result.email} has been subscribed.</p>}
      </div>
    )
  }

  return (
    <fetcher.Form
      id="mailing-list-form"
      method="post"
      action="/subscribe"
      className="flex flex-col items-center gap-4 md:gap-2 w-full md:p-4"
    >
      <div className="w-3/4 md:w-1/2 xl:w-1/3 2xl:w-1/4">
        <label className="flex flex-col text-center gap-1">
          Email
          <input
            defaultValue={result?.email ?? ""}
            name="email"
            placeholder="email@example.com"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            spellCheck={false}
            required
            aria-invalid={errorMessage ? true : undefined}
            aria-describedby={errorMessage ? errorId : undefined}
            className="text-lg text-center w-full p-2 rounded-xl bg-white/10 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2 placeholder-white/50 hover:bg-white/20 transition-shadow transition-color duration-300 ease-in-out shadow-none hover:shadow-black/20 hover:shadow-md"
          />
        </label>
      </div>
      <input type="hidden" name="source" value={source} />
      <HoneypotInputs nonce={nonce} />
      <button
        type="submit"
        disabled={isSubmitting}
        className="flex items-center justify-center text-white text-center uppercase font-bold text-xl w-3/4 md:w-1/2 xl:w-1/3 2xl:w-1/4 py-2 md:text-xl rounded-2xl bg-orange transition-all duration-300 ease-in-out shadow-none hover:shadow-orange/50 hover:shadow-lg hover:scale-105 disabled:opacity-75 disabled:scale-100 disabled:cursor-not-allowed"
      >
        {isSubmitting
          ? <Emblem className="size-7 animate-pulse text-white" />
          : 'Subscribe'
        }
      </button>
      {errorMessage && (
        <div
          id={errorId}
          role="alert"
          className="flex justify-center items-center gap-2 w-3/4 md:w-1/2 xl:w-1/3 2xl:w-1/4 px-4 py-3 rounded-xl bg-orange/15 border border-orange/50 text-center text-sm text-white"
        >
          <Icon name="triangle-warning" size="md" className="shrink-0 text-orange" title="Warning" />
          {errorMessage}
        </div>
      )}
    </fetcher.Form>
  );
}
